import { Theme } from './theme';

/**
 * Icon Style Variants
 * -----------------
 * tinted (aka "soft", "ghost", "outlined-fill"):
 *   - Solid icon color
 *   - Light/transparent background (same hue)
 *   - Optional border with stronger opacity
 *
 * filled (aka "solid", "contained"):
 *   - White icon color
 *   - Solid background color
 *   - No border (or same as background)
 */
export type IconStyleVariant = 'tinted' | 'filled';

export interface IconContainerStyle {
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  iconColor: string;
}

/**
 * Returns icon container styles based on variant and base color.
 * Use this for consistent icon styling across the app.
 *
 * @param color - The base color (hex string)
 * @param variant - 'tinted' or 'filled'
 * @param options - Optional overrides (bgOpacity, borderOpacity, borderWidth)
 */
export const getIconContainerStyle = (
  color: string,
  variant: IconStyleVariant,
  options?: {
    bgOpacity?: number;
    borderOpacity?: number;
    borderWidth?: number;
  },
): IconContainerStyle => {
  const {
    bgOpacity = 0.2,
    borderOpacity = 0.25,
    borderWidth = 1,
  } = options ?? {};

  if (variant === 'filled') {
    return {
      backgroundColor: color,
      borderColor: color,
      borderWidth: 0,
      iconColor: '#FFFFFF',
    };
  }

  // tinted variant (default)
  return {
    backgroundColor: withOpacity(color, bgOpacity),
    borderColor: withOpacity(color, borderOpacity),
    borderWidth,
    iconColor: color,
  };
};

// Utility to create styles with theme
export const createThemedStyles = <T extends Record<string, any>>(
  styleCreator: (theme: Theme) => T,
) => styleCreator;

// Color utilities
export const getColor = (theme: Theme, colorKey: keyof Theme['colors']) => {
  return theme.colors[colorKey];
};

// Spacing utilities
export const getSpacing = (
  theme: Theme,
  spacingKey: keyof Theme['spacing'],
) => {
  return theme.spacing[spacingKey];
};

// Multiple spacing values
export const getSpacings = (
  theme: Theme,
  ...spacingKeys: Array<keyof Theme['spacing']>
) => {
  return spacingKeys.map(key => theme.spacing[key]);
};

// Border radius utilities
export const getBorderRadius = (
  theme: Theme,
  radiusKey: keyof Theme['borderRadius'],
) => {
  return theme.borderRadius[radiusKey];
};

// Shadow utilities
export const getShadow = (theme: Theme, shadowKey: keyof Theme['shadows']) => {
  return theme.shadows[shadowKey];
};

// Typography utilities
export const getTypography = (
  theme: Theme,
  typographyKey: keyof Theme['typography'],
) => {
  return theme.typography[typographyKey];
};

// Opacity utilities
export const withOpacity = (color: string, opacity: number) => {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const alpha = Math.round(opacity * 255)
      .toString(16)
      .padStart(2, '0');
    return `#${hex}${alpha}`;
  }

  // Handle rgba/rgb colors - convert to rgba
  if (color.startsWith('rgb')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }

  return color;
};

// Create component variants
export const createVariant = (
  theme: Theme,
  variant:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'premium',
) => {
  const baseColors = {
    primary: { bg: theme.colors.primary, text: theme.colors.textInverse },
    secondary: { bg: theme.colors.secondary, text: theme.colors.textInverse },
    success: { bg: theme.colors.success, text: theme.colors.textInverse },
    warning: { bg: theme.colors.warning, text: theme.colors.text },
    error: { bg: theme.colors.error, text: theme.colors.textInverse },
    info: { bg: theme.colors.info, text: theme.colors.textInverse },
    premium: { bg: theme.colors.premium, text: theme.colors.textInverse },
  };

  return baseColors[variant];
};

// Common component style patterns
export const cardStyle = (theme: Theme) => ({
  backgroundColor: theme.colors.surface,
  borderRadius: theme.borderRadius.lg,
  padding: theme.spacing.lg,
  ...theme.shadows.md,
});

export const buttonStyle = (
  theme: Theme,
  variant: 'primary' | 'secondary' = 'primary',
) => {
  const colors = createVariant(theme, variant);
  return {
    backgroundColor: colors.bg,
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    ...theme.shadows.sm,
  };
};

export const inputStyle = (theme: Theme) => ({
  backgroundColor: theme.colors.surface,
  borderColor: theme.colors.border,
  borderWidth: 1,
  borderRadius: theme.borderRadius.md,
  paddingVertical: theme.spacing.md,
  paddingHorizontal: theme.spacing.md,
  color: theme.colors.text,
  fontSize: theme.typography.body.fontSize,
});

// Device-responsive utilities
export const deviceStyle = <T>(tablet: T, phone: T, isTablet: boolean): T => {
  return isTablet ? tablet : phone;
};

// Animation easing curves (commonly used with theme)
export const easingCurves = {
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
};
